import clsx from 'clsx'
import type { ElementType, PropsWithChildren } from 'react'
import React from 'react'

interface TypographyProps {
  /**
   * The color of the text
   * @default 'initial'
   */
  color?: TypographyColor
  /**
   * The component used for the root node
   * @default 'span'
   */
  component?: ElementType
  /**
   * The id of the element.
   * @default undefined
   */
  id?: string
  /**
   * If true, the overflow text will be truncated with an ellipsis.
   * Recommended to avoid truncation as it typically causes accessibility issues
   * @default false
   */
  truncate?: boolean
  /**
   * The visual style variant to apply.
   * This controls the font size, weight, and line height, but not the HTML tag used.
   * @default 'body1'
   */
  variant?: TypographyVariant
}

type TypographyColor = 'initial' | 'inherit' | 'inverse'

type TypographyColors = {
  [key in TypographyColor]: string
}

type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'subtitle3'
  | 'body1'
  | 'body2'
  | 'button'
  | 'caption'
  | 'overline'

type TypographyVariants = {
  [key in TypographyVariant]: string
}

const colors: TypographyColors = {
  inherit: 'text-inherit',
  initial: 'text-black-90',
  inverse: 'text-white-90',
}

const variants: TypographyVariants = {
  h1: `text-6xl font-medium tracking-tight`,
  h2: `text-5xl font-medium tracking-tight`,
  h3: `text-4xl font-medium tracking-normal`,
  h4: `text-3xl font-medium tracking-normal`,
  h5: `text-2xl font-medium tracking-normal`,
  h6: `text-xl font-medium tracking-wide`,
  subtitle1: `text-base font-medium tracking-normal`,
  subtitle2: `text-sm font-medium tracking-wide`,
  subtitle3: `text-xs font-medium tracking-wider`,
  body1: `text-base font-normal tracking-normal`,
  body2: `text-sm font-normal tracking-wide`,
  button: `text-sm font-medium tracking-wide`,
  caption: `text-xs font-normal tracking-wider`,
  overline: `text-xs font-medium tracking-widest uppercase`,
}

function Typography({
  children,
  color = 'initial',
  component: Component = 'span',
  id,
  truncate = false,
  variant = 'body1',
}: PropsWithChildren<TypographyProps>): JSX.Element {
  return (
    <Component
      className={clsx(
        'font-sans',
        colors[color],
        truncate ? 'truncate' : null,
        variants[variant],
      )}
      id={id}
    >
      {children}
    </Component>
  )
}

export default Typography
